package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.Route
import com.siriusxm.pia.aggregatorAdmin
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.rest.unifiedaggregator.asEntity
import com.siriusxm.pia.views.unifiedaggregator.backfill.backfill
import com.siriusxm.pia.views.unifiedaggregator.backfill.backfillSection
import com.siriusxm.pia.views.unifiedaggregator.backfill.startBackfill
import com.siriusxm.pia.views.unifiedaggregator.replay.replay
import com.siriusxm.pia.views.unifiedaggregator.replay.startReplay
import com.siriusxm.pia.views.unifiedaggregator.batch.batchJob
import com.siriusxm.pia.views.unifiedaggregator.dataflow.dataFlowVisualization
import com.siriusxm.pia.views.unifiedaggregator.editor.EntityEditor
import com.siriusxm.pia.views.unifiedaggregator.events.EventFilters
import com.siriusxm.pia.views.unifiedaggregator.management.*
import com.siriusxm.pia.operations.components.OperationsStyles
import com.siriusxm.pia.operations.dashboard.DashboardStyles
import com.siriusxm.pia.operations.dashboard.StatCardStyles
import com.siriusxm.pia.views.unifiedaggregator.operations.Operations
import com.siriusxm.pia.views.unifiedaggregator.relationships.relationshipEditor
import com.siriusxm.pia.views.unifiedaggregator.relationships.relationships
import com.siriusxm.pia.views.unifiedaggregator.types.dataTypes
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * Routing for the aggregator functionality.
 */
@Composable
fun Route.contentAggregator(
    application: AggregatorApplication
) {
    val aggregatorService by application.aggregatorContext()

    Style(OperationsStyles)
    Style(DashboardStyles)
    Style(StatCardStyles)

    val loadError = GlobalAggregatorLoader.error

    CompositionLocalProvider(LocalAppState provides application) {
        if (aggregatorService == null) {
            serviceView({}) {
                if (loadError != null) {
                    // Show error state with retry button
                    Div({
                        style {
                            textAlign("center")
                            marginTop(2.em)
                        }
                    }) {
                        Div({
                            style {
                                color(Color.red)
                                fontWeight(700)
                                fontSize(1.3.em)
                                marginBottom(1.em)
                            }
                        }) {
                            Text("Failed to load aggregator data")
                        }
                        Div({
                            style {
                                fontSize(0.9.em)
                                marginBottom(1.em)
                                color(Color.gray)
                            }
                        }) {
                            Text(loadError.message ?: "Unknown error")
                        }
                        button("Retry", primary = true) {
                            GlobalAggregatorLoader.reload()
                        }
                    }
                } else {
                    Div({
                        style {
                            textAlign("center")
                            paddingTop(3.em)
                            paddingBottom(3.em)
                        }
                    }) {
                        spinner(size = Size.LARGE)
                    }
                }
            }
        } else {
            val aggregatorContext = aggregatorService!!
            val windowWidth = window.innerWidth
            serviceView({
                simpleNavSidebar(
                    windowWidth >= PhoneCutoffWidth, "Aggregator", listOfNotNull(
                        NavigationItem("Entities", "aggregator"),
                        NavigationItem("Types", "aggregator/types"),
                        NavigationItem("Backfill", "aggregator/backfill"),
                        NavigationItem("Relationships", "aggregator/relationships"),
                        NavigationItem("Producers", "aggregator/producers"),
                        NavigationItem("Consumers", "aggregator/consumers"),
                        NavigationItem("Features", "aggregator/features"),
                        NavigationItem("Operations", "aggregator/operations"),
                        NavigationItem("Audit Log", "aggregator/audit")
                    )
                )
            }) {
                switch {
                    select("entity") {
                        switch {
                            select("new") {
                                EntityEditor(null)
                            }
                            select(Regex(".+")) {
                                val entityId = this.match

                                switch {
                                    select("partials") {
                                        switch {
                                            serviceContentView {
                                                select(Regex(".*")) {
                                                    partialEditor(aggregatorContext, entityId, match.ifBlank { null })
                                                }
                                            }
                                        }
                                    }
                                    select("edit") {
                                        EntityEditor(entityId)
                                    }
                                    // Select a specific tab
                                    select(Regex(".*")) {
                                        val destination = EntityDetailDestination(
                                            entityId,
                                            EntityTab.fromId(match.ifBlank { null })
                                        )
                                        aggregatorEntity(
                                            aggregatorContext,
                                            destination,
                                            mapOf("expand" to parameter("expand")),
                                            EventFilters.from(Query.parse(fullPath.substringAfter("?", "")))
                                        )
                                    }
                                }
                            }
                        }
                    }

                    select("types") {
                        dataTypes(aggregatorContext, this)
                    }

                    select("relationships") {
                        switch {
                            select("edit") {
                                var entity by remember { mutableStateOf<Entity?>(null) }
                                val entityId = parameter("entity")
                                LaunchedEffect(entityId) {
                                    if (entityId != entity?.id) {
                                        entity = if (entityId != null) {
                                            aggregatorContext.clientApi.getEntityById(entityId).firstOrNull()
                                                ?.asEntity()
                                        } else {
                                            null
                                        }
                                    }
                                }

                                serviceContentView({}) {
                                    if (entityId != null && entityId != entity?.id) {
                                        dialogView("Create Relationship") {
                                            content {
                                                spinner()
                                            }
                                        }
                                    } else {
                                        relationshipEditor(
                                            aggregatorContext,
                                            schema = parameter("relationship")?.let { relationshipId ->
                                                aggregatorContext.relationships.find { it.id == relationshipId }
                                            },
                                            entity = entity,
                                            returnPath = parameter("return")
                                        )
                                    }
                                }
                            }
                            select(Regex(".*")) {
                                relationships(aggregatorContext, match.ifBlank { null })
                            }
                        }
                    }

                    // The producer subtree is a pilot of typed destination keys (UCA-1028): rather
                    // than interleaving matching and rendering in nested switch/select blocks, the
                    // remaining path is decoded to a ProducerKey and dispatched by a flat `when`.
                    // See ProducerNavigation.kt. Every other subtree still uses the DSL below.
                    //
                    // The query has to be handed over separately: `remainingPath` never carries one,
                    // because cmc-core splits path from query at the root of the tree. We read it
                    // from `fullPath` — which is on the Route *interface* and is propagated unchanged
                    // to every child — rather than from DefaultRoute.query (needs a downcast, and is
                    // computed with `substringAfter("?")` with no fallback, so it holds the whole
                    // path when there is no query) or from per-name `parameter()` calls (which map a
                    // literal "null" value to null and drop any pair whose value contains an
                    // unencoded `=`). Parsing once here also means the producer subtree sees one
                    // immutable Query rather than reaching back into the router.
                    select("producers") {
                        ProducerRoutes(
                            aggregatorContext,
                            remainingPath,
                            Query.parse(fullPath.substringAfter("?", ""))
                        )
                    }

                    select("consumers") {
                        switch {
                            select("edit") {
                                serviceContentView({}) {
                                    aggregatorContext.context.capabilityRoute(
                                        ConsumerCreateCapability,
                                        operation = "create consumers"
                                    ) {
                                        consumerEditor(aggregatorContext)
                                    }
                                }
                            }
                            select(Regex(".*")) {
                                val consumer = aggregatorContext.consumerById(match)
                                switch {
                                    select("edit") {
                                        serviceContentView({}) {
                                            aggregatorContext.context.capabilityRoute(
                                                consumerUpdateCapability(match),
                                                operation = "update this consumer"
                                            ) {
                                                consumer?.let {
                                                    consumerEditor(aggregatorContext, it)
                                                }
                                            }
                                        }
                                    }
                                    default {
                                        consumers(aggregatorContext, consumer?.id)
                                    }
                                }
                            }
                        }
                    }

                    select("data-flow") {
                        dataFlowVisualization(aggregatorContext)
                    }

                    select("backfill") {
                        switch {
                            select("new") {
                                serviceContentView {
                                    startBackfill(
                                        aggregatorContext,
                                        parameter("target"),
                                        parameter("role"),
                                        parameter("types")?.split(",")?.mapNotNull { type ->
                                            aggregatorContext.entityTypes.find { it.type == type }
                                        })
                                }
                            }

                            select("new-replay") {
                                serviceContentView {
                                    startReplay(aggregatorContext)
                                }
                            }

                            select("replays") {
                                switch {
                                    select(Regex(".+")) {
                                        replay(aggregatorContext, match)
                                    }

                                    default {
                                        backfillSection(aggregatorContext, "replays")
                                    }
                                }
                            }

                            select("recent") {
                                backfillSection(aggregatorContext, "recent")
                            }

                            select(Regex(".+")) {
                                backfill(aggregatorContext, match)
                            }

                            default {
                                backfillSection(aggregatorContext, "recent")
                            }
                        }
                    }

                    select("search") {
                        entities(
                            aggregatorContext,
                            this.parameter("query"),
                            this.parameter("type"),
                            this.parameter("visible")?.toBooleanStrictOrNull()
                        )
                    }

                    select("batch") {
                        batchJob(aggregatorContext)
                    }

                    select("features") {
                        featureFlags(aggregatorContext, aggregatorContext.viewer.aggregatorAdmin)
                    }

                    select("operations") {
                        Operations()
                    }

                    select("audit") {
                        ContentAuditLog(aggregatorContext, Query.parse(fullPath.substringAfter("?", "")))
                    }

                    select("transcript-test") {
                        serviceContentView {
                            transcriptTest()
                        }
                    }

                    default {
                        entities(aggregatorContext)
                    }
                }
            }
        }
    }
}
